import React, { useState, useEffect } from "react";
import { PieChart, Pie, Tooltip, ResponsiveContainer, Cell } from "recharts";

const BillingReport = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [billingData, setBillingData] = useState(null);
  const [activeTab, setActiveTab] = useState("summary");

  useEffect(() => {
    loadBillingData();
  }, []);

  const loadBillingData = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await fetch("/api/get-billing");

      if (!response.ok) {
        throw new Error(`API error: ${response.statusText}`);
      }

      const contentType = response.headers.get("content-type");
      if (!contentType || !contentType.includes("application/json")) {
        throw new Error("Server returned non-JSON response");
      }

      const data = await response.json();
      console.log("========== FULL API RESPONSE ==========");
      console.log("[BILLING] Complete data object:", data);
      console.log("[BILLING] Budget:", data.monthlyBudget);
      console.log("[BILLING] Cost Total Spent:", data.cost?.totalSpent);
      console.log("[BILLING] Full Cost Response:", JSON.stringify(data.cost, null, 2));
      console.log("[BILLING] Usage data:", JSON.stringify(data.usage, null, 2));
      console.log("[BILLING] All keys in response:", Object.keys(data));
      if (data.cost?.rawData?.data) {
        console.log("[BILLING] Daily cost breakdown:", data.cost.rawData.data);
      }
      if (data.usage?.data) {
        console.log("[BILLING] Token usage by model:", data.usage.data);
      }
      console.log("========== END API RESPONSE ==========");

      const budget = data.monthlyBudget || 100;

      // Process usage and cost data from API
      let totalSpent = 0;
      let totalInputTokens = 0;
      let totalOutputTokens = 0;
      const modelBreakdown = {};
      let costByModel = {};

      // Get real cost data if available from API
      if (data.cost?.totalSpent !== undefined) {
        totalSpent = parseFloat(data.cost.totalSpent);
        console.log("[BILLING] Real cost data from API - Total spent:", totalSpent);
      }

      if (data.usage?.data && Array.isArray(data.usage.data)) {
        data.usage.data.forEach(period => {
          if (period.usage && Array.isArray(period.usage)) {
            period.usage.forEach(usage => {
              const model = usage.model || "Unknown";
              const inputTokens = usage.input_tokens || 0;
              const outputTokens = usage.output_tokens || 0;

              totalInputTokens += inputTokens;
              totalOutputTokens += outputTokens;

              if (!modelBreakdown[model]) {
                modelBreakdown[model] = {
                  model,
                  inputTokens: 0,
                  outputTokens: 0,
                  totalTokens: 0,
                  cost: 0
                };
              }

              modelBreakdown[model].inputTokens += inputTokens;
              modelBreakdown[model].outputTokens += outputTokens;
              modelBreakdown[model].totalTokens += inputTokens + outputTokens;

              // Use real cost from API if available
              let modelCost = costByModel[model] || 0;
              modelBreakdown[model].cost += modelCost;
            });
          }
        });
      }

      // Calculate metrics from budget and actual spending
      const remaining = Math.max(0, budget - totalSpent);
      const percentUsed = (totalSpent / budget) * 100;

      // Cost efficiency
      const costPerToken = totalInputTokens + totalOutputTokens > 0 ? (totalSpent / (totalInputTokens + totalOutputTokens)) * 1000000 : 0;

      setBillingData({
        budget,
        spent: totalSpent,
        remaining,
        percentUsed: Math.min(100, percentUsed),
        totalTokens: totalInputTokens + totalOutputTokens,
        inputTokens: totalInputTokens,
        outputTokens: totalOutputTokens,
        modelBreakdown: Object.values(modelBreakdown).sort((a, b) => b.cost - a.cost),
        costPerToken
      });

      setLoading(false);
    } catch (err) {
      console.error("Error loading billing data:", err);
      setError(err.message || "Failed to load billing data");
      setLoading(false);
    }
  };

  if (loading) {
    return <div style={{ padding: "20px", color: "#7A9CC4" }}>Loading billing data...</div>;
  }

  if (error) {
    return <div style={{ padding: "20px", color: "#EF4444" }}>Error: {error}</div>;
  }

  if (!billingData) {
    return <div style={{ padding: "20px", color: "#7A9CC4" }}>No billing data available</div>;
  }

  const COLORS = ["#3B82F6", "#10B981", "#F59E0B", "#EF4444", "#8B5CF6", "#EC4899", "#06B6D4"];

  const budgetColor = billingData.percentUsed < 80 ? "#10B981" : billingData.percentUsed < 100 ? "#F59E0B" : "#EF4444";

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "24px", width: "100%" }}>
      {/* Tab Navigation */}
      <div style={{ display: "flex", gap: "8px", borderBottom: "1px solid rgba(59, 130, 246, 0.2)", paddingBottom: "12px", alignItems: "center", justifyContent: "space-between" }}>
        <div style={{ display: "flex", gap: "8px" }}>
          {[
            { id: "summary", label: "Summary" },
            { id: "models", label: "Models" }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              style={{
                padding: "8px 16px",
                background: activeTab === tab.id ? "rgba(59, 130, 246, 0.2)" : "transparent",
                border: activeTab === tab.id ? "1px solid rgba(59, 130, 246, 0.4)" : "none",
                borderRadius: "6px",
                color: activeTab === tab.id ? "#5B9EF0" : "#7A9CC4",
                cursor: "pointer",
                fontSize: "13px",
                fontWeight: activeTab === tab.id ? "600" : "400",
                transition: "all 0.2s"
              }}
            >
              {tab.label}
            </button>
          ))}
        </div>

        <a
          href="https://console.anthropic.com"
          target="_blank"
          rel="noopener noreferrer"
          style={{
            display: "inline-block",
            padding: "8px 16px",
            background: "rgba(255, 149, 0, 0.15)",
            border: "1px solid rgba(255, 149, 0, 0.3)",
            borderRadius: "6px",
            color: "#FF9500",
            textDecoration: "none",
            fontSize: "13px",
            fontWeight: "600",
            transition: "all 0.2s",
            cursor: "pointer"
          }}
          onMouseEnter={(e) => {
            e.target.style.background = "rgba(255, 149, 0, 0.25)";
            e.target.style.borderColor = "rgba(255, 149, 0, 0.5)";
          }}
          onMouseLeave={(e) => {
            e.target.style.background = "rgba(255, 149, 0, 0.15)";
            e.target.style.borderColor = "rgba(255, 149, 0, 0.3)";
          }}
        >
          Claude Console
        </a>
      </div>

      {/* SUMMARY TAB */}
      {activeTab === "summary" && (
        <div style={{ display: "flex", flexDirection: "column", gap: "24px" }}>
          {/* Budget Status */}
          <div style={{
            background: "rgba(59, 130, 246, 0.05)",
            border: "1px solid rgba(59, 130, 246, 0.15)",
            borderRadius: "8px",
            padding: "24px"
          }}>
            <h2 style={{ color: "#5B9EF0", margin: "0 0 20px 0" }}>Budget Status</h2>

            <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: "16px", marginBottom: "20px" }}>
              <div style={{ background: "rgba(59, 130, 246, 0.08)", padding: "12px", borderRadius: "6px", textAlign: "center" }}>
                <div style={{ color: "#7A9CC4", fontSize: "11px" }}>Monthly Budget</div>
                <div style={{ color: "#5B9EF0", fontSize: "16px", fontWeight: "600" }}>${billingData.budget.toFixed(2)}</div>
              </div>
              <div style={{ background: "rgba(59, 130, 246, 0.08)", padding: "12px", borderRadius: "6px", textAlign: "center" }}>
                <div style={{ color: "#7A9CC4", fontSize: "11px" }}>Amount Spent</div>
                <div style={{ color: "#EF4444", fontSize: "16px", fontWeight: "600" }}>${billingData.spent.toFixed(2)}</div>
              </div>
              <div style={{ background: "rgba(59, 130, 246, 0.08)", padding: "12px", borderRadius: "6px", textAlign: "center" }}>
                <div style={{ color: "#7A9CC4", fontSize: "11px" }}>Remaining</div>
                <div style={{ color: "#10B981", fontSize: "16px", fontWeight: "600" }}>${billingData.remaining.toFixed(2)}</div>
              </div>
              <div style={{ background: "rgba(59, 130, 246, 0.08)", padding: "12px", borderRadius: "6px", textAlign: "center" }}>
                <div style={{ color: "#7A9CC4", fontSize: "11px" }}>Usage %</div>
                <div style={{ color: budgetColor, fontSize: "16px", fontWeight: "600" }}>{billingData.percentUsed.toFixed(1)}%</div>
              </div>
            </div>

            {/* Progress Bar */}
            <div style={{ marginBottom: "20px" }}>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "8px" }}>
                <span style={{ color: "#7A9CC4", fontSize: "12px" }}>Budget Usage</span>
                <span style={{ color: budgetColor, fontSize: "12px", fontWeight: "600" }}>{billingData.percentUsed.toFixed(1)}%</span>
              </div>
              <div style={{
                width: "100%",
                height: "8px",
                background: "rgba(59, 130, 246, 0.1)",
                borderRadius: "4px",
                overflow: "hidden"
              }}>
                <div style={{
                  width: `${Math.min(100, billingData.percentUsed)}%`,
                  height: "100%",
                  background: budgetColor,
                  transition: "width 0.3s ease"
                }} />
              </div>
            </div>
          </div>

          {/* Token Summary */}
          <div style={{
            background: "rgba(59, 130, 246, 0.05)",
            border: "1px solid rgba(59, 130, 246, 0.15)",
            borderRadius: "8px",
            padding: "24px"
          }}>
            <h2 style={{ color: "#5B9EF0", margin: "0 0 20px 0" }}>Token Usage</h2>

            <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "16px" }}>
              <div style={{ background: "rgba(59, 130, 246, 0.08)", padding: "16px", borderRadius: "6px", textAlign: "center" }}>
                <div style={{ color: "#7A9CC4", fontSize: "11px", marginBottom: "8px" }}>Input Tokens</div>
                <div style={{ color: "#5B9EF0", fontSize: "18px", fontWeight: "600" }}>{(billingData.inputTokens / 1000000).toFixed(2)}M</div>
              </div>
              <div style={{ background: "rgba(59, 130, 246, 0.08)", padding: "16px", borderRadius: "6px", textAlign: "center" }}>
                <div style={{ color: "#7A9CC4", fontSize: "11px", marginBottom: "8px" }}>Output Tokens</div>
                <div style={{ color: "#5B9EF0", fontSize: "18px", fontWeight: "600" }}>{(billingData.outputTokens / 1000000).toFixed(2)}M</div>
              </div>
              <div style={{ background: "rgba(59, 130, 246, 0.08)", padding: "16px", borderRadius: "6px", textAlign: "center" }}>
                <div style={{ color: "#7A9CC4", fontSize: "11px", marginBottom: "8px" }}>Total Tokens</div>
                <div style={{ color: "#5B9EF0", fontSize: "18px", fontWeight: "600" }}>{(billingData.totalTokens / 1000000).toFixed(2)}M</div>
              </div>
            </div>

            {/* Cost Efficiency */}
            <div style={{ marginTop: "20px", padding: "12px 16px", background: "rgba(59, 130, 246, 0.08)", borderRadius: "6px", textAlign: "center" }}>
              <div style={{ color: "#7A9CC4", fontSize: "12px" }}>Cost per Million Tokens</div>
              <div style={{ color: "#5B9EF0", fontSize: "16px", fontWeight: "600" }}>${billingData.costPerToken.toFixed(4)}</div>
            </div>
          </div>
        </div>
      )}

      {/* MODELS TAB */}
      {activeTab === "models" && (
        <div style={{ display: "flex", flexDirection: "column", gap: "24px" }}>
          {/* Token Distribution */}
          <div style={{
            background: "rgba(59, 130, 246, 0.05)",
            border: "1px solid rgba(59, 130, 246, 0.15)",
            borderRadius: "8px",
            padding: "24px"
          }}>
            <h2 style={{ color: "#5B9EF0", margin: "0 0 20px 0" }}>Token Distribution by Model</h2>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={billingData.modelBreakdown}
                  dataKey="totalTokens"
                  nameKey="model"
                  cx="50%"
                  cy="50%"
                  label
                >
                  {billingData.modelBreakdown.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip
                  contentStyle={{ background: "rgba(5, 14, 26, 0.9)", border: "1px solid rgba(59, 130, 246, 0.3)" }}
                  labelStyle={{ color: "#5B9EF0" }}
                  formatter={(value) => `${(value / 1000000).toFixed(2)}M tokens`}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>

          {/* Model Comparison Table */}
          <div style={{
            background: "rgba(59, 130, 246, 0.05)",
            border: "1px solid rgba(59, 130, 246, 0.15)",
            borderRadius: "8px",
            padding: "24px",
            overflowX: "auto"
          }}>
            <h2 style={{ color: "#5B9EF0", margin: "0 0 20px 0" }}>Model Comparison</h2>
            <table style={{
              width: "100%",
              borderCollapse: "collapse",
              fontSize: "13px"
            }}>
              <thead>
                <tr style={{ borderBottom: "1px solid rgba(59, 130, 246, 0.2)" }}>
                  <th style={{ textAlign: "left", padding: "8px", color: "#7A9CC4" }}>Model</th>
                  <th style={{ textAlign: "right", padding: "8px", color: "#7A9CC4" }}>Input Tokens</th>
                  <th style={{ textAlign: "right", padding: "8px", color: "#7A9CC4" }}>Output Tokens</th>
                  <th style={{ textAlign: "right", padding: "8px", color: "#7A9CC4" }}>Total Tokens</th>
                  <th style={{ textAlign: "right", padding: "8px", color: "#7A9CC4" }}>Cost</th>
                  <th style={{ textAlign: "right", padding: "8px", color: "#7A9CC4" }}>% of Total</th>
                </tr>
              </thead>
              <tbody>
                {billingData.modelBreakdown.map((model, idx) => (
                  <tr key={idx} style={{ borderBottom: "1px solid rgba(59, 130, 246, 0.1)" }}>
                    <td style={{ padding: "8px", color: "#9DB4D4", display: "flex", alignItems: "center", gap: "8px" }}>
                      <div style={{
                        width: "12px",
                        height: "12px",
                        background: COLORS[idx % COLORS.length],
                        borderRadius: "2px",
                        flexShrink: 0
                      }} />
                      {model.model.replace("claude-", "").split("-").slice(0, 2).join("-")}
                    </td>
                    <td style={{ textAlign: "right", padding: "8px", color: "#9DB4D4" }}>
                      {(model.inputTokens / 1000000).toFixed(2)}M
                    </td>
                    <td style={{ textAlign: "right", padding: "8px", color: "#9DB4D4" }}>
                      {(model.outputTokens / 1000000).toFixed(2)}M
                    </td>
                    <td style={{ textAlign: "right", padding: "8px", color: "#9DB4D4" }}>
                      {(model.totalTokens / 1000000).toFixed(2)}M
                    </td>
                    <td style={{ textAlign: "right", padding: "8px", color: "#10B981", fontWeight: "600" }}>
                      ${model.cost.toFixed(2)}
                    </td>
                    <td style={{ textAlign: "right", padding: "8px", color: "#7A9CC4" }}>
                      {billingData.spent > 0 ? ((model.cost / billingData.spent) * 100).toFixed(1) : 0}%
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default BillingReport;
